import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CameraIcon, CheckIcon, IdCardIcon, MapPinIcon, PlusIcon, SendIcon, ShieldCheckIcon, UploadIcon, UserRoundIcon, UsersRoundIcon, XIcon, BoxIcon } from "lucide-react";
import { editableFields, familyMembers, memberValues, verifiedAddresses, FamilyMember } from "../../data/carte";
const ease = [0.23, 1, 0.32, 1] as const;
const fieldIcons: Record<string, BoxIcon> = {
  prenom: UserRoundIcon,
  nom: IdCardIcon,
  genre: UsersRoundIcon,
  photo: CameraIcon,
  adresse: MapPinIcon
};
interface EditCardDialogProps {
  member: FamilyMember | null;
  onClose: () => void;
}
type Step = 'champs' | 'formulaire' | 'preuves' | 'confirmation';
export function EditCardDialog({
  member,
  onClose
}: EditCardDialogProps) {
  const [step, setStep] = useState<Step>('champs');
  const [selected, setSelected] = useState<string[]>([]);
  const [alsoFor, setAlsoFor] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [addressChoice, setAddressChoice] = useState<string>(verifiedAddresses[0].id);
  const [newAddress, setNewAddress] = useState('');
  const [proofs, setProofs] = useState<string[]>([]);
  const current = member ? memberValues[member.id] ?? {} : {};
  useEffect(() => {
    if (member) {
      setStep('champs');
      setSelected([]);
      setAlsoFor([]);
      setValues(memberValues[member.id] ?? {});
      setAddressChoice(verifiedAddresses[0].id);
      setNewAddress('');
      setProofs([]);
    }
  }, [member]);
  if (!member) return null;
  const chosen = editableFields.filter(field => selected.includes(field.id));
  const addressSelected = selected.includes('adresse');
  const newAddressChosen = addressSelected && addressChoice === 'nouvelle';
  const needProof = [...chosen.filter(field => field.requiresProof), ...(newAddressChosen ? [editableFields.find(field => field.id === 'adresse')!] : [])];
  const others = familyMembers.filter(item => item.id !== member.id);
  const toggle = (list: string[], id: string) => list.includes(id) ? list.filter(item => item !== id) : [...list, id];
  const next = () => {
    if (step === 'champs') setStep('formulaire');else if (step === 'formulaire') setStep('preuves');else setStep('confirmation');
  };
  const back = () => {
    if (step === 'formulaire') setStep('champs');else if (step === 'preuves') setStep('formulaire');else onClose();
  };
  const stepIndex = ['champs', 'formulaire', 'preuves'].indexOf(step);
  return <Shell title={member.id === 'bryon' ? 'Edit my card' : `Edit ${member.firstName}’s card`} onClose={onClose} data-id="element-464" data-mp="ez1rkei1s9ho">
      {step !== 'confirmation' && <ol className="mb-6 flex items-center gap-2" aria-label="Steps" data-id="element-465" data-mp="50cldizikdh9">
          {['Fields', 'Details', 'Validation'].map((label, index) => <li key={label} className="flex flex-1 items-center gap-2" data-id="element-466" data-mp="2upy0pl9ph8kr" data-mpi={index}>
              <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold ${index <= stepIndex ? 'bg-ville-indigo text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-467" data-mp="2pquvo0tq72cs">
                {index + 1}
              </span>
              <span className={`text-xs font-semibold ${index <= stepIndex ? 'text-ville-navy' : 'text-slate-400'}`} data-id="element-468" data-mp="2kmizf6cgaw3f">
                {label}
              </span>
            </li>)}
        </ol>}

      <AnimatePresence mode="wait" data-id="element-469" data-mp="2ucm0lttq7gcu">
        <motion.div key={step} initial={{
        opacity: 0,
        x: 16
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -16
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-470" data-mp="2udnd82qe4awo">
          {step === 'champs' && <div data-id="element-471" data-mp="38cmzx4ucefq7">
              <p className="text-sm text-slate-600" data-id="element-472" data-mp="38c3n22jjuhj1">Which details would you like to change?</p>

              <ul className="mt-4 grid grid-cols-2 gap-3" data-id="element-473" data-mp="3d9n8bmenzukh">
                {editableFields.map(field => {
              const Icon = fieldIcons[field.id];
              const active = selected.includes(field.id);
              return <li key={field.id} data-id="element-474" data-mp="1iy8hf29x3p35">
                      <button type="button" onClick={() => setSelected(list => toggle(list, field.id))} aria-pressed={active} className={`relative flex h-full w-full flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-475" data-mp="39cpwd4332y35">
                        <span className={`grid h-9 w-9 place-items-center rounded-xl ${active ? 'bg-ville-indigo text-white' : 'bg-slate-100 text-ville-navy'}`} aria-hidden="true" data-id="element-476" data-mp="2pnfizwqt8p87">
                          <Icon className="h-4 w-4" data-id="element-477" data-mp="2zgwa2szkbq86" />
                        </span>
                        <span className="text-[15px] font-bold text-ville-navy" data-id="element-478" data-mp="2untfqimyocqx">{field.label}</span>
                        <span className="text-xs text-slate-500" data-id="element-479" data-mp="26xpnt8j4y1fr">{current[field.id]}</span>
                        {active && <span className="absolute right-3 top-3 grid h-5 w-5 place-items-center rounded-full bg-ville-indigo text-white" aria-hidden="true" data-id="element-480" data-mp="21z61h70mqnmv">
                            <CheckIcon className="h-3.5 w-3.5" data-id="element-481" data-mp="m8d9xbv9plth" />
                          </span>}
                      </button>
                    </li>;
            })}
              </ul>
            </div>}

          {step === 'formulaire' && <div className="space-y-4" data-id="element-482" data-mp="dhcyklz1usjx">
              {chosen.filter(field => field.id !== 'adresse').map(field => <div key={field.id} data-id="element-483" data-mp="116xjvbioe1wd">
                    <label htmlFor={`champ-${field.id}`} className="block text-sm font-bold text-ville-navy" data-id="element-484" data-mp="113rpphv6w9ts">
                      {field.label}
                    </label>
                    <input id={`champ-${field.id}`} type="text" value={values[field.id] ?? ''} onChange={event => setValues({
              ...values,
              [field.id]: event.target.value
            })} placeholder={field.placeholder} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-[15px] text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-485" data-mp="r76mn22qd8sl" />
                    <p className="mt-1 text-xs text-slate-500" data-id="element-486" data-mp="2st2bdqu0akvp">Current value: {current[field.id]}</p>
                  </div>)}

              {addressSelected && <fieldset data-id="element-487" data-mp="2o0r76bs8gcxc">
                  <legend className="text-sm font-bold text-ville-navy" data-id="element-488" data-mp="2j3ipsdewscx5">Address</legend>
                  <ul className="mt-2 space-y-2" data-id="element-489" data-mp="2j1odtolm7v57">
                    {verifiedAddresses.map(address => {
                const active = addressChoice === address.id;
                return <li key={address.id} data-id="element-490" data-mp="293rxmidtma08">
                          <button type="button" onClick={() => setAddressChoice(address.id)} aria-pressed={active} className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-491" data-mp="253g9gvm91rk0">
                            <MapPinIcon className={`mt-0.5 h-5 w-5 shrink-0 ${active ? 'text-ville-indigo' : 'text-slate-400'}`} aria-hidden="true" data-id="element-492" data-mp="5xkrvmetqqgq" />
                            <span className="min-w-0" data-id="element-493" data-mp="atszmeqozhdv">
                              <span className="block text-[15px] font-semibold text-ville-navy" data-id="element-494" data-mp="fnni5euk9qbd">{address.label}</span>
                              <span className="block text-xs text-slate-500" data-id="element-495" data-mp="kl5tfbw1whmj">{address.note}</span>
                            </span>
                          </button>
                        </li>;
              })}

                    <li data-id="element-496" data-mp="3o2o0vi7wvmmf">
                      <button type="button" onClick={() => setAddressChoice('nouvelle')} aria-pressed={newAddressChosen} className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${newAddressChosen ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-dashed border-slate-300 hover:border-ville-indigo'}`} data-id="element-497" data-mp="3o48sayyxipg2">
                        <PlusIcon className={`h-5 w-5 shrink-0 ${newAddressChosen ? 'text-ville-indigo' : 'text-slate-400'}`} aria-hidden="true" data-id="element-498" data-mp="1yfgjbptjwle9" />
                        <span className="text-[15px] font-semibold text-ville-navy" data-id="element-499" data-mp="1tdhgqi5mxsid">Add a new address</span>
                      </button>
                    </li>
                  </ul>

                  {newAddressChosen && <div className="mt-3" data-id="element-500" data-mp="2h4wlehkvwlws">
                      <label htmlFor="nouvelle-adresse" className="sr-only" data-id="element-501" data-mp="2h3vcainfi9hp">
                        New address
                      </label>
                      <input id="nouvelle-adresse" type="text" value={newAddress} onChange={event => setNewAddress(event.target.value)} placeholder="Number, street, town" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-[15px] text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-502" data-mp="2c866vyoq9ydh" />
                      <p className="mt-1 text-xs text-slate-500" data-id="element-503" data-mp="rs1jk6q128c1">
                        A new address must be verified by the Town at the next step.
                      </p>
                    </div>}

                  <div className="mt-4 rounded-2xl bg-slate-50 px-5 py-4" data-id="element-504" data-mp="38ehkdpyf1m03">
                    <p className="text-sm font-bold text-ville-navy" data-id="element-505" data-mp="2tmza88s8mlon">Change it as well for…</p>
                    <ul className="mt-2 space-y-1.5" data-id="element-506" data-mp="2ylahqcv28k3p">
                      {others.map(item => {
                  const active = alsoFor.includes(item.id);
                  return <li key={item.id} data-id="element-507" data-mp="2ooxgmtx6r3yx">
                            <button type="button" onClick={() => setAlsoFor(list => toggle(list, item.id))} aria-pressed={active} disabled={!item.canEdit} className="flex w-full items-center gap-3 rounded-xl px-2 py-1.5 text-left transition-colors duration-150 ease-ville hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent" data-id="element-508" data-mp="2opz0nnw6j0ss">
                              <span className={`grid h-5 w-5 shrink-0 place-items-center rounded border ${active ? 'border-ville-indigo bg-ville-indigo text-white' : item.canEdit ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-100'}`} aria-hidden="true" data-id="element-509" data-mp="3f7o4z2thrr6f">
                                {active && <CheckIcon className="h-3.5 w-3.5" data-id="element-510" data-mp="364n1zazxag6z" />}
                              </span>
                              <img src={item.photo} alt="" className="h-7 w-7 rounded-full object-cover" data-id="element-511" data-mp="3b3gb6r86fl1c" />
                              <span className="text-sm font-semibold text-ville-navy" data-id="element-512" data-mp="6rm8uobnkwah">{item.firstName}</span>
                              {!item.canEdit && <span className="ml-auto text-xs text-slate-500" data-id="element-513" data-mp="3oy0nkphqxa9x">Consent required</span>}
                            </button>
                          </li>;
                })}
                    </ul>
                  </div>
                </fieldset>}
            </div>}

          {step === 'preuves' && needProof.length === 0 && <div className="text-center" data-id="element-514" data-mp="2ihyca34o9jft">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-515" data-mp="2axmcs46o3mbw">
                <ShieldCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-516" data-mp="25zch7sh16jpr" />
              </span>
              <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-517" data-mp="2jyu94gpmid3c">
                Changes saved
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-518" data-mp="2ezr3au1tk2ub">
                Your card is up to date. The changes are visible right away.
              </p>
              {alsoFor.length > 0 && <p className="mt-2 text-sm text-slate-500" data-id="element-519" data-mp="1n9mn77l3e1yw">
                  Also applied to{' '}
                  {alsoFor.map(id => familyMembers.find(item => item.id === id)?.firstName).filter(Boolean).join(', ')}
                  .
                </p>}
            </div>}

          {step === 'preuves' && needProof.length > 0 && <div data-id="element-520" data-mp="18h2lmbdlc2jk">
              <p className="text-sm text-slate-600" data-id="element-521" data-mp="13k2fy1knl1sz">
                These details must be validated by the Town. Attach a document for each one.
              </p>
              <ul className="mt-4 space-y-3" data-id="element-522" data-mp="33m20c2qggz6r">
                {needProof.map(field => {
              const done = proofs.includes(field.id);
              return <li key={field.id} className="rounded-2xl border border-slate-200 px-5 py-4" data-id="element-523" data-mp="3dcdd8es4aips">
                      <p className="text-[15px] font-bold text-ville-navy" data-id="element-524" data-mp="3iayfejqxjcgv">{field.label}</p>
                      <p className="mt-0.5 text-sm text-slate-500" data-id="element-525" data-mp="2knpp8bfzhf8j">{field.proofLabel}</p>
                      <button type="button" onClick={() => setProofs(list => toggle(list, field.id))} className={`mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${done ? 'bg-ville-indigo-soft text-ville-indigo' : 'border border-slate-200 text-ville-navy hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-526" data-mp="2pmj5ucqkhmcx">
                        {done ? <CheckIcon className="h-4 w-4" data-id="element-527" data-mp="dvf77o7v53hu" /> : <UploadIcon className="h-4 w-4" data-id="element-528" data-mp="1yf13w8clvfnu" />}
                        {done ? 'Document attached' : 'Attach a document'}
                      </button>
                    </li>;
            })}
              </ul>
            </div>}

          {step === 'confirmation' && <div className="text-center" data-id="element-529" data-mp="1pl5yr0bhhp87">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-530" data-mp="1knvzyu27g17p">
                <ShieldCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-531" data-mp="1fot33wwdp1at" />
              </span>
              <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-532" data-mp="1ap6r1qsadr3l">
                {needProof.length > 0 ? 'Request sent' : 'Changes saved'}
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-533" data-mp="11p1njrq7rcup">
                {needProof.length > 0 ? 'The Town will review the documents you provided. You will be notified as soon as the card is updated.' : 'Your card is up to date. The changes are visible right away.'}
              </p>
              {alsoFor.length > 0 && <p className="mt-2 text-sm text-slate-500" data-id="element-534" data-mp="1ssyd713yrzau">
                  Also applied to{' '}
                  {alsoFor.map(id => familyMembers.find(item => item.id === id)?.firstName).filter(Boolean).join(', ')}
                  .
                </p>}
            </div>}
        </motion.div>
      </AnimatePresence>

      <div className="mt-7 flex items-center justify-between gap-4" data-id="element-535" data-mp="1ooqp965byndo">
        {step === 'confirmation' || step === 'preuves' && needProof.length === 0 ? <button type="button" onClick={onClose} className="ml-auto rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-536" data-mp="1eruesuvvfr6h">
            Done
          </button> : <>
            <button type="button" onClick={back} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-537" data-mp="r31r1lpekpmy">
              {step === 'champs' ? 'Cancel' : 'Back'}
            </button>
            <button type="button" onClick={next} disabled={step === 'champs' && selected.length === 0} className="rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:hover:translate-y-0" data-id="element-538" data-mp="7ikpvy4pfbz6">
              {step === 'formulaire' && needProof.length === 0 ? 'Save' : step === 'preuves' ? 'Submit' : 'Continue'}

            </button>
          </>}
      </div>
    </Shell>;
}
interface ConsentDialogProps {
  member: FamilyMember | null;
  onClose: () => void;
}
export function ConsentDialog({
  member,
  onClose
}: ConsentDialogProps) {
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (member) setSent(false);
  }, [member]);
  if (!member) return null;
  return <Shell title={`Edit ${member.firstName}’s card`} onClose={onClose} data-id="element-539" data-mp="1h4otqmbsqk6d">
      {sent ? <div className="text-center" data-id="element-540" data-mp="1m6e748mhr9xm">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-541" data-mp="1r4z31vupn7g9">
            <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-542" data-mp="1vvxmh4azwlnq" />
          </span>
          <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-543" data-mp="1zxj6rapv027y">Request sent</h3>
          <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-544" data-mp="24w40qseippny">
            {member.firstName} will get a notification and can let you edit their card.
          </p>
        </div> : <div className="flex items-start gap-4" data-id="element-545" data-mp="2eujp4nm5uktr">
          <img src={member.photo} alt="" className="h-14 w-14 shrink-0 rounded-full object-cover" data-id="element-546" data-mp="2et8noug6tp5t" />
          <p className="text-[15px] leading-relaxed text-slate-600" data-id="element-547" data-mp="fiqzxd5m6jrl">
            You do not have {member.firstName}’s consent to edit their card.
          </p>
        </div>}

      <div className="mt-7 flex items-center justify-between gap-4" data-id="element-548" data-mp="2xrbqqpjt7177">
        <button type="button" onClick={onClose} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-549" data-mp="2xsmuipd988yb">
          {sent ? 'Close' : 'Cancel'}
        </button>
        {!sent && <button type="button" onClick={() => setSent(true)} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-550" data-mp="2jxklhtd423ha">
            <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-551" data-mp="32ottfy78gk6r" />
            Ask for their consent
          </button>}
      </div>
    </Shell>;
}
function Shell({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease
  }} data-id="element-552" data-mp="3rd93cebqxpvm">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-553" data-mp="3hfuy7av9wnim" />
      <motion.div role="dialog" aria-modal="true" aria-label={title} initial={{
      y: 20,
      opacity: 0,
      scale: 0.98
    }} animate={{
      y: 0,
      opacity: 1,
      scale: 1
    }} exit={{
      y: 12,
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease
    }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-7 shadow-2xl sm:rounded-3xl" data-id="element-554" data-mp="3meemhhxcabe5">
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-555" data-mp="959vmryzm10f">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-556" data-mp="2hezlepjakuj6" />
        </button>
        <h2 className="pr-10 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-557" data-mp="2hepv10uc4lcy">{title}</h2>
        <div className="mt-5" data-id="element-558" data-mp="2w32q7vllm06e">{children}</div>
      </motion.div>
    </motion.div>;
}